import { useEffect, useRef } from 'react';
import { hasAnalyticsConsent } from '@/utils/cookieConsent';

// Hotjar Site ID
const HOTJAR_ID = 6510861;
const HOTJAR_VERSION = 6;

// Debug mode for development
const DEBUG_MODE = process.env.NODE_ENV === 'development';

declare global {
  interface Window {
    hj: {
      (...args: any[]): void;
      q?: any[];
    };
    _hjSettings: {
      hjid: number;
      hjsv: number;
    };
    hjLoaded?: boolean;
  }
}

const Hotjar = () => {
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    const loadHotjarScript = () => {
      // Only load Hotjar if user has consented to analytics
      if (!hasAnalyticsConsent()) {
        if (DEBUG_MODE) console.log('Hotjar: No analytics consent, skipping script load');
        return;
      }

      // Prevent duplicate script loading
      if (scriptLoadedRef.current || window.hjLoaded) {
        if (DEBUG_MODE) console.log('Hotjar: Script already loaded, skipping');
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector(`script[src*="static.hotjar.com/c/hotjar-${HOTJAR_ID}"]`);
      if (existingScript) {
        if (DEBUG_MODE) console.log('Hotjar: Script element already exists');
        scriptLoadedRef.current = true;
        window.hjLoaded = true;
        return;
      }

      if (DEBUG_MODE) console.log('Hotjar: Loading script for site', HOTJAR_ID);

      // Load Hotjar script using the exact pattern from their instructions
      (function(h, o, t, j, a, r) {
        h.hj = h.hj || function() { (h.hj.q = h.hj.q || []).push(arguments) };
        h._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_VERSION };
        a = o.getElementsByTagName('head')[0];
        r = o.createElement('script');
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        
        r.onload = () => {
          if (DEBUG_MODE) console.log('Hotjar: Script loaded successfully');
          scriptLoadedRef.current = true;
          window.hjLoaded = true;
        };

        r.onerror = (error) => {
          console.error('Hotjar: Failed to load script', error);
          console.error('Hotjar: This might be due to CSP blocking or network issues');
        };

        a.appendChild(r);
      })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');

      if (DEBUG_MODE) {
        console.log('Hotjar: Configured with ID', HOTJAR_ID, 'version', HOTJAR_VERSION);
      }
    };

    // Load script initially
    loadHotjarScript();

    // Listen for consent changes
    const handleConsentChange = (event: CustomEvent) => {
      if (DEBUG_MODE) console.log('Hotjar: Consent changed', event.detail);
      if (event.detail.status === 'accepted') {
        // Small delay to ensure the consent state is updated
        setTimeout(loadHotjarScript, 100);
      }
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange as EventListener);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange as EventListener);
      if (DEBUG_MODE) console.log('Hotjar: Component unmounting, keeping script');
    };
  }, []);

  return null;
};

// Custom tracking functions for Hotjar with V2.0 parameters
export const identifyUser = (userId: string, attributes?: Record<string, any>) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('Hotjar: No analytics consent, skipping identify', userId);
    return;
  }
  
  if (window.hj) {
    const enhancedAttributes = {
      ...attributes,
      app_version: '2.0',
      layout_version: 'interactive_preview'
    };
    window.hj('identify', userId, enhancedAttributes);
    if (DEBUG_MODE) console.log('Hotjar: User identified', userId, enhancedAttributes);
  }
};

export const trackEvent = (eventName: string) => {
  if (!hasAnalyticsConsent()) {
    if (DEBUG_MODE) console.log('Hotjar: No analytics consent, skipping event', eventName);
    return;
  }
  
  if (window.hj) {
    window.hj('event', `${eventName}_v2`);
    if (DEBUG_MODE) console.log('Hotjar: Event tracked', `${eventName}_v2`);
  }
};

export const addUserAttributes = (attributes: Record<string, any>) => {
  if (window.hj) {
    const enhancedAttributes = {
      ...attributes,
      app_version: '2.0',
      layout_version: 'interactive_preview'
    };
    window.hj('identify', enhancedAttributes);
    if (DEBUG_MODE) console.log('Hotjar: User attributes added', enhancedAttributes);
  }
};

// New V2.0 specific Hotjar tracking functions
export const trackInteractivePreviewHj = (action: string, templateId?: string) => {
  if (window.hj) {
    window.hj('event', `interactive_preview_${action}_v2`);
    if (DEBUG_MODE) console.log('Hotjar: Interactive preview tracked', action, templateId);
  }
};

export const trackConversionFunnelHj = (step: string, stepNumber: number) => {
  if (window.hj) {
    window.hj('event', `funnel_step_${stepNumber}_${step}_v2`);
    if (DEBUG_MODE) console.log('Hotjar: Funnel step tracked', step, stepNumber);
  }
};

export const trackFormEngagementHj = (formType: string, engagement: string) => {
  if (window.hj) {
    window.hj('event', `form_${formType}_${engagement}_v2`);
    if (DEBUG_MODE) console.log('Hotjar: Form engagement tracked', formType, engagement);
  }
};

export default Hotjar;